// Merge-field resolution for canned email templates (register item 9).
// PURE — no React, no Firestore — so it bundles into the node test harness.
// Pinned by scripts/utest-merge-fields.mjs.
//
// Resolution happens at INSERT time, not send time. The sender always sees the
// final text in the compose box before it goes out, so a wrong or missing value
// is caught by a human rather than mailed to a client.

import { fmtDate, tsToDate } from './format.js'

/**
 * The fields a template may reference. Deliberately a closed list: an open
 * `{{anything}}` would let a typo ship silently, and this set is small enough to
 * show in full next to the editor.
 */
export const MERGE_FIELDS = [
  { token: 'contact.firstName', label: "Contact's first name" },
  { token: 'contact.lastName', label: "Contact's last name" },
  { token: 'contact.fullName', label: "Contact's full name" },
  { token: 'contact.email', label: "Contact's email" },
  { token: 'engagement.title', label: 'Engagement title' },
  { token: 'engagement.org', label: 'Client / org' },
  { token: 'engagement.date', label: 'Engagement date' },
  { token: 'engagement.location', label: 'Engagement location' },
  { token: 'sender.name', label: 'Your name' },
]

export const MERGE_TOKENS = MERGE_FIELDS.map((f) => f.token)

const TOKEN_RE = /\{\{\s*([a-zA-Z]+\.[a-zA-Z]+)\s*\}\}/g

/** Values for every known token, given whatever context the caller has. */
export function buildMergeContext({ contact, engagement, sender } = {}) {
  const c = contact || {}
  const e = engagement || {}
  const s = sender || {}
  const first = (c.firstName || '').trim()
  const last = (c.lastName || '').trim()
  return {
    'contact.firstName': first,
    'contact.lastName': last,
    'contact.fullName': [first, last].filter(Boolean).join(' '),
    'contact.email': (c.email || '').trim(),
    'engagement.title': (e.title || '').trim(),
    'engagement.org': (e.org || '').trim(),
    'engagement.date': fmtDate(tsToDate(e.date)) || '',
    'engagement.location': (e.location || '').trim(),
    'sender.name': (s.name || '').trim(),
  }
}

/** Every token a piece of template text references, in order, deduped. */
export function tokensIn(text) {
  const out = []
  String(text || '').replace(TOKEN_RE, (_m, token) => { if (!out.includes(token)) out.push(token); return _m })
  return out
}

/**
 * Substitute tokens into `text`.
 *
 * An unknown token (a typo like `{{contact.frstName}}`) is LEFT IN PLACE rather
 * than blanked — a visible `{{contact.frstName}}` in the compose box tells the
 * sender something is wrong, whereas silently deleting it produces a sentence
 * that reads fine and is missing a name.
 *
 * A known-but-empty token resolves to '' and is reported in `unresolved`, so the
 * UI can warn before send.
 */
export function applyMergeFields(text, context = {}) {
  const unresolved = []
  const unknown = []
  const body = String(text || '').replace(TOKEN_RE, (match, token) => {
    if (!(token in context)) {
      if (!unknown.includes(token)) unknown.push(token)
      return match
    }
    const value = context[token]
    if (!value) {
      if (!unresolved.includes(token)) unresolved.push(token)
      return ''
    }
    return value
  })
  return { text: body, unresolved, unknown }
}

/** Resolve a whole template's subject + body against one context. */
export function renderTemplate(template, context = {}) {
  const subject = applyMergeFields(template?.subject, context)
  const body = applyMergeFields(template?.body, context)
  const merge = (a, b) => [...new Set([...a, ...b])]
  return {
    subject: subject.text,
    body: body.text,
    unresolved: merge(subject.unresolved, body.unresolved),
    unknown: merge(subject.unknown, body.unknown),
    placeholders: remainingPlaceholders({ subject: subject.text, body: body.text }),
  }
}

/**
 * Everything the compose modal drops in when a template is picked: rendered
 * subject/body plus the template's default Cc/Bcc as editable comma-joined
 * text. A template with no Cc/Bcc yields '' so picking it clears the previous
 * template's recipients.
 */
export function draftFromTemplate(template, context = {}) {
  const list = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : '')
  return { ...renderTemplate(template, context), cc: list(template?.cc), bcc: list(template?.bcc) }
}

// ── Fill-in placeholders (Templates v2) ──────────────────────────────────────
// A blank that no merge field can answer — "scheduling link", "specific detail
// tied to what surfaced" — is written `[[label]]`. Unlike `{{tokens}}` it is
// never substituted: the sender must replace it by hand, and the compose modal
// refuses to Send while any remain. Single-line only, so a stray `[[` can't
// swallow a paragraph.

const PLACEHOLDER_RE = /\[\[([^\[\]\r\n]*)\]\]/g

/** Every `[[label]]` in `text`, trimmed, in order, deduped; empty `[[]]` ignored. */
export function placeholdersIn(text) {
  const out = []
  String(text || '').replace(PLACEHOLDER_RE, (m, label) => {
    const l = label.trim()
    if (l && !out.includes(l)) out.push(l)
    return m
  })
  return out
}

/** Placeholders still present across a draft's subject + body. */
export function remainingPlaceholders({ subject, body } = {}) {
  return [...new Set([...placeholdersIn(subject), ...placeholdersIn(body)])]
}

/** Human-readable "fill these in" line, or '' when none remain. */
export function placeholderWarning(labels = []) {
  if (!labels.length) return ''
  return `Fill in ${labels.length} blank${labels.length > 1 ? 's' : ''}: ${labels.map((l) => `[[${l}]]`).join(', ')}`
}

// ── Workflow grouping (Templates v2) ─────────────────────────────────────────
// Templates carry an optional free-text `workflow` ("Consulting", "Speaking"…),
// a `step` label, and a numeric `order`. Groups sort by their earliest step so
// a workflow reads in send order; templates without a workflow land in a
// trailing '' group.

const wfKey = (w) => String(w || '').trim().toLowerCase()
const hasOrder = (t) => typeof t.order === 'number' && Number.isFinite(t.order)
const byStep = (a, b) => {
  if (hasOrder(a) && hasOrder(b) && a.order !== b.order) return a.order - b.order
  if (hasOrder(a) !== hasOrder(b)) return hasOrder(a) ? -1 : 1
  return String(a.name || '').localeCompare(String(b.name || ''))
}

/** [{ workflow, templates }] — workflow '' (ungrouped) always last. */
export function groupTemplates(templates = []) {
  const groups = new Map()
  templates.forEach((t) => {
    const key = wfKey(t.workflow)
    if (!groups.has(key)) groups.set(key, { workflow: key ? String(t.workflow).trim() : '', templates: [] })
    groups.get(key).templates.push(t)
  })
  const list = [...groups.values()]
  list.forEach((g) => g.templates.sort(byStep))
  const minOrder = (g) => Math.min(...g.templates.filter(hasOrder).map((t) => t.order), Infinity)
  return list.sort((a, b) => {
    if (!a.workflow !== !b.workflow) return a.workflow ? -1 : 1
    const ma = minOrder(a), mb = minOrder(b) // Infinity when a group has no ordered step
    if (ma !== mb) return ma < mb ? -1 : 1
    return a.workflow.localeCompare(b.workflow)
  })
}

/**
 * Templates offered for a given engagement type. An empty/absent `types` means
 * the template is global — the common case, and the default in the editor.
 */
export function templatesForType(templates = [], type) {
  return templates.filter((t) => !Array.isArray(t.types) || t.types.length === 0 || (!!type && t.types.includes(type)))
}

/** Human-readable warning line, or '' when everything resolved. */
export function warningFor({ unresolved = [], unknown = [] } = {}) {
  const parts = []
  if (unresolved.length) parts.push(`no value for ${unresolved.join(', ')}`)
  if (unknown.length) parts.push(`unknown field${unknown.length > 1 ? 's' : ''} ${unknown.join(', ')} left as-is`)
  return parts.join(' · ')
}
